import { useState, MouseEvent } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { UserWithRoles, UserOptions } from '../../types/User';

const options: UserOptions[] = [
  { value: 'Editor', label: 'Can Edit' },
  { value: 'Reader', label: 'Can View' },
];

interface PermissionsMenuProps {
  member: UserWithRoles;
  onUpdate: (member: UserWithRoles, option: UserOptions) => void;
  onRemove: (member: UserWithRoles) => void;
}

export function PermissionsMenu({ member, onUpdate, onRemove }: PermissionsMenuProps) {
  const { role, identifier } = member;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  const roleLabel = (role: string) => options.find(option => option.value === role)?.label;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      {role === 'Owner' ? (
        <IconButton color="primary">
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {role}
          </Typography>
        </IconButton>
      ) : (
        <>
          <IconButton color="primary" onClick={handleOpenMenu} data-testid={`role-${identifier}`}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {roleLabel(role ?? '') || 'None'}
            </Typography>
            <KeyboardArrowDownRoundedIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleCloseMenu}
            id={identifier}
            aria-label="permissions-menu"
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  minWidth: 160,
                  overflow: 'visible',
                  filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.25))',
                },
              },
            }}
            transformOrigin={{
              horizontal: 'right',
              vertical: 'top',
            }}
            anchorOrigin={{
              horizontal: 'right',
              vertical: 'bottom',
            }}
          >
            {options.map(option => {
              const hideOption = option.value === 'Owner' && identifier === 'allUsers';
              if (hideOption) {
                return null;
              }
              return (
                <MenuItem
                  key={option.value}
                  onClick={() => {
                    onUpdate(member, option);
                    handleCloseMenu();
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'start',
                  }}
                  disabled={role === option.value}
                >
                  {role === option.value && (
                    <CheckIcon fontSize="small" sx={{ color: 'text.primary', mr: 1 }} />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {option.label}
                  </Typography>
                </MenuItem>
              );
            })}
            <MenuItem
              onClick={() => {
                onRemove(member);
                handleCloseMenu();
              }}
            >
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 'bold' }}>
                Remove
              </Typography>
            </MenuItem>
          </Menu>
        </>
      )}
    </Box>
  );
}

export default PermissionsMenu;
